import {
  Box,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import React from 'react';
import type { Holder, Tenant } from '../data/types';
import { date } from '../lib/format';

interface Props {
  tenant: Tenant;
  title: string;
  subtitle?: string;
  holder?: Holder;
  issued: string;
  children: React.ReactNode;
  footnote?: string;
}

export default function DocumentPaper({
  tenant,
  title,
  subtitle,
  holder,
  issued,
  children,
  footnote,
}: Props) {
  return (
    <Box
      sx={{
        bgcolor: '#FFFFFF',
        color: '#1E293B',
        border: '1px solid #E2E8F0',
        borderRadius: 1,
        p: { xs: 2.5, sm: 4 },
        fontSize: '0.85rem',
      }}
    >
      <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography
            sx={{
              fontFamily: "'Manrope', sans-serif",
              fontWeight: 800,
              fontSize: '1.3rem',
              color: tenant.accent,
            }}
          >
            {tenant.shortName}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            {tenant.name} · ABN {tenant.abn}
          </Typography>
          <Typography variant="caption" color="text.secondary" component="div">
            {tenant.supportEmail}
          </Typography>
        </Box>
        <Box sx={{ textAlign: 'right' }}>
          <Typography variant="caption" color="text.secondary" component="div">
            Issued {date(issued)}
          </Typography>
          {holder && (
            <Typography variant="caption" color="text.secondary" component="div">
              Account {holder.accountNumber}
            </Typography>
          )}
        </Box>
      </Box>
      {holder && (
        <Box sx={{ mt: 3 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>
            {holder.name}
          </Typography>
          <Typography variant="body2">{holder.address.line1}</Typography>
          <Typography variant="body2">
            {`${holder.address.suburb} ${holder.address.state} ${holder.address.postcode}`.trim()}
          </Typography>
        </Box>
      )}
      <Typography
        sx={{ fontFamily: "'Manrope', sans-serif", fontWeight: 800, fontSize: '1.25rem', mt: 3 }}
      >
        {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary">
          {subtitle}
        </Typography>
      )}
      <Divider sx={{ my: 2 }} />
      {children}
      <Divider sx={{ my: 2 }} />
      <Typography variant="caption" color="text.secondary" component="div">
        {footnote ?? 'Keep this document for your records.'} Prototype document generated from mock
        data — illustrative only, not tax or financial advice.
      </Typography>
    </Box>
  );
}

export function DocTable({
  head,
  rows,
  total,
}: {
  head: string[];
  rows: (string | number)[][];
  total?: (string | number)[];
}) {
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ '& td, & th': { px: 1, fontSize: '0.8rem' } }}>
        <TableHead>
          <TableRow>
            {head.map((cell, index) => (
              <TableCell key={cell} align={index === 0 ? 'left' : 'right'} sx={{ fontWeight: 700 }}>
                {cell}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row, rowIndex) => (
            <TableRow key={rowIndex}>
              {row.map((cell, index) => (
                <TableCell key={index} align={index === 0 ? 'left' : 'right'}>
                  {cell}
                </TableCell>
              ))}
            </TableRow>
          ))}
          {total && (
            <TableRow>
              {total.map((cell, index) => (
                <TableCell
                  key={index}
                  align={index === 0 ? 'left' : 'right'}
                  sx={{ fontWeight: 700, borderBottom: 0 }}
                >
                  {cell}
                </TableCell>
              ))}
            </TableRow>
          )}
        </TableBody>
      </Table>
    </Box>
  );
}
